// Campaign security utilities for authorization and ownership verification

import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Verify if the current user owns a specific campaign
 * @param {string} campaignId - The campaign ID to check
 * @param {string} companyId - The current user's company ID
 * @returns {Promise<{authorized: boolean, error?: string, campaign?: object}>}
 */
export const verifyCampaignOwnership = async (campaignId, companyId) => {
  try {
    if (!campaignId) {
      return { authorized: false, error: 'Campaign ID is required' };
    }

    if (!companyId) {
      return { authorized: false, error: 'Authentication required' };
    }

    const docRef = doc(db, "campaign", campaignId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return { authorized: false, error: 'Campaign not found' };
    }

    const campaignData = docSnap.data();
    
    if (campaignData.companyId !== companyId) {
      return { authorized: false, error: 'You do not have permission to access this campaign' };
    }

    return { 
      authorized: true, 
      campaign: { id: campaignId, ...campaignData } 
    };
  } catch (error) {
    console.error('Error verifying campaign ownership:', error);
    return { authorized: false, error: 'Failed to verify campaign access' };
  }
};

/**
 * Verify if the current user owns a specific client company
 * @param {string} clientId - The client company ID to check
 * @param {string} parentCompanyId - The current user's company ID (parent)
 * @returns {Promise<{authorized: boolean, error?: string, client?: object}>}
 */
export const verifyClientOwnership = async (clientId, parentCompanyId) => {
  try {
    if (!clientId) {
      return { authorized: false, error: 'Client ID is required' };
    }

    if (!parentCompanyId) {
      return { authorized: false, error: 'Authentication required' };
    }

    const docRef = doc(db, "workspace", clientId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return { authorized: false, error: 'Client not found' };
    }

    const clientData = docSnap.data();
    
    if (clientData.parentCompany !== parentCompanyId || clientData.type !== 'client') {
      return { authorized: false, error: 'You do not have permission to access this client' };
    }

    return { 
      authorized: true, 
      client: { id: clientId, ...clientData } 
    };
  } catch (error) {
    console.error('Error verifying client ownership:', error);
    return { authorized: false, error: 'Failed to verify client access' };
  }
};

/**
 * Rate limiting for sensitive operations
 */
const OPERATION_RATE_LIMITS = {
  'campaign_delete': { maxOperations: 5, windowMs: 60 * 60 * 1000 }, // 5 deletions per hour
  'client_delete': { maxOperations: 3, windowMs: 60 * 60 * 1000 },   // 3 client deletions per hour
  'campaign_update': { maxOperations: 50, windowMs: 60 * 60 * 1000 } // 50 updates per hour
};

/**
 * Check if operation is within rate limits
 * @param {string} operationType - Type of operation (campaign_delete, client_delete, etc.)
 * @param {string} userId - User identifier for rate limiting
 * @returns {{allowed: boolean, waitTime?: number, message?: string}}
 */
export const checkOperationRateLimit = (operationType, userId) => {
  const config = OPERATION_RATE_LIMITS[operationType];
  if (!config) {
    return { allowed: true }; // No rate limiting configured
  }

  const key = `${operationType}_${userId}`;
  const now = Date.now();
  
  try {
    const stored = localStorage.getItem(key);
    const operations = stored ? JSON.parse(stored) : [];
    
    // Filter to only recent operations within the time window
    const recentOperations = operations.filter(timestamp => now - timestamp < config.windowMs);
    
    if (recentOperations.length >= config.maxOperations) {
      const oldestOperation = Math.min(...recentOperations);
      const waitTime = Math.ceil((oldestOperation + config.windowMs - now) / 1000 / 60); // minutes
      
      return {
        allowed: false,
        waitTime,
        message: `Rate limit exceeded. Please wait ${waitTime} minutes before trying again.`
      };
    }
    
    return { allowed: true };
  } catch (error) {
    console.error('Error checking rate limit:', error);
    return { allowed: true }; // Allow on error to avoid blocking users
  }
};

/**
 * Record a successful operation for rate limiting
 * @param {string} operationType - Type of operation
 * @param {string} userId - User identifier
 */
export const recordOperation = (operationType, userId) => {
  const config = OPERATION_RATE_LIMITS[operationType];
  if (!config) return;

  const key = `${operationType}_${userId}`;
  const now = Date.now();
  
  try {
    const stored = localStorage.getItem(key);
    const operations = stored ? JSON.parse(stored) : [];
    
    // Add new operation and clean old ones
    const recentOperations = operations.filter(timestamp => now - timestamp < config.windowMs);
    recentOperations.push(now);
    
    localStorage.setItem(key, JSON.stringify(recentOperations));
  } catch (error) {
    console.error('Error recording operation:', error);
  }
}; 